import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useListingContext } from '../../../contexts/ListingContext';
import styles from '../../../styles/ListingManagement.module.css';
import viewStyles from '../../../styles/ViewListing.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import PropertyDetails from './PropertyDetails';
import ClientDetails from './ClientDetails';
import ListingImages from './ListingImages';
import ListingDocuments from './ListingDocuments';
import LoadingSpinner from 'components/common/LoadingSpinner';

const ViewListing: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { openListing, activeListing, clearActiveListing, deleteListing } = useListingContext();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    openListing(id)
      .then(() => setLoading(false))
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Failed to load listing';
        setError(msg);
        setLoading(false);
      });
    return () => clearActiveListing();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <LoadingSpinner />
    );
  }

  if (error || !activeListing) {
    return (
      <div className={viewStyles.page}>
        <p className={styles.transactionEmpty}>{error ?? 'Listing not found.'}</p>
        <button
          className={`${buttonStyles.button} ${buttonStyles.secondary}`}
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>
    );
  }

  const detail = activeListing;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteListing(detail.id);
      navigate('/ListingManagement');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to delete listing';
      setError(msg);
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const statusBadgeClass: Record<string, string> = {
    Open:       viewStyles.badgeOpen,
    Pending:    viewStyles.badgePending,
    Closed:     viewStyles.badgeClosed,
    Incomplete: viewStyles.badgeIncomplete,
  };

  const addressParts = [
    detail.unitNumber ? `Unit ${detail.unitNumber}` : null,
    detail.complexName,
    `${detail.streetNumber} ${detail.streetName}`,
    detail.city,
  ].filter(Boolean).join(', ');

  return (
    <div className={viewStyles.page}>
      {/* ── Header ── */}
      <div className={viewStyles.header}>
        <div className={viewStyles.headerLeft}>
          <h1 className={viewStyles.address}>{addressParts}</h1>
          <div className={viewStyles.badges}>
            <span className={`${viewStyles.badge} ${viewStyles.badgeType}`}>{detail.type}</span>
            <span className={`${viewStyles.badge} ${statusBadgeClass[detail.status] ?? viewStyles.badgeClosed}`}>
              {detail.status}
            </span>
          </div>
        </div>
        <div className={viewStyles.headerActions}>
          <button
            className={`${buttonStyles.button} ${buttonStyles.secondary}`}
            onClick={() => navigate(-1)}
          >
            Back
          </button>
          <button
            className={`${buttonStyles.button} ${buttonStyles.primary}`}
            onClick={() => navigate(`/EditListing/${detail.id}`)}
          >
            Edit
          </button>
          <button
            className={`${buttonStyles.button} ${buttonStyles.danger}`}
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete
          </button>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#fff', borderRadius: 10, padding: '32px 28px',
            maxWidth: 420, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <h3 style={{ marginTop: 0 }}>Delete Listing?</h3>
            <p style={{ color: '#495057' }}>
              This will permanently delete the listing, all its clients, client documents,
              listing documents, and images. This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                className={`${buttonStyles.button} ${buttonStyles.secondary}`}
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className={`${buttonStyles.button} ${buttonStyles.danger}`}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <PropertyDetails detail={detail}/>

      <ClientDetails clients={detail.clients}/>

      <ListingImages images={detail.images}/>

      <ListingDocuments documents={detail.documents}/>
    </div>
  );
};

export default ViewListing;
